import { useEffect, useRef, useState } from "react";
import { Loader2, ExternalLink } from "lucide-react";
import { BOOKING_URL } from "@/config/site";

// Official Vagaro loader (do not alter URL or widget configuration).
const VAGARO_SCRIPT_SRC =
  "https://www.vagaro.com//resources/WidgetEmbeddedLoader/OZqpCJ4uE3CcT3qmV35y6JuPlXgz3Py79oz34mC2PeFJ4mC30m9cyUfZApiB6yI1JS64cTpqt9d0yep2sERAJDXwOapcUbfY?v=akz5yY6Hu9yhP6ikdrOj8D8I7zeKDsYSOeLyZZNHYtQu#";

// Powered-by markup exactly as supplied in the official embed.
const POWERED_BY_HTML = `<style>.vagaro a {font-size:14px; color:#AAA; text-decoration:none;}</style><a href="https://www.vagaro.com/pro/">Powered by Vagaro</a>&nbsp;<a href="https://www.vagaro.com/pro/salon-software">Salon Software</a>,&nbsp;<a href="https://www.vagaro.com/pro/spa-software">Spa Software</a>&nbsp;&amp;&nbsp;<a href="https://www.vagaro.com/pro/fitness-software">Fitness Software</a>`;

/**
 * Loads the official Vagaro booking widget after mount.
 * - Injects the loader script once per mount and cleans up on unmount so
 *   navigating away and back does not duplicate the widget.
 * - Guards against React StrictMode double-invocation.
 * - Provides an accessible loading state and an external fallback.
 * - Does not intercept, log, transmit or store any booking information.
 */
export const VagaroBookingWidget = () => {
  const containerRef = useRef(null);
  const startedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || startedRef.current) return;
    startedRef.current = true;

    // Fresh render each mount — prevents duplicate widgets.
    container.innerHTML = "";

    const title = document.createElement("div");
    title.id = "frameTitle";
    title.className = "embedded-widget-title";
    title.setAttribute(
      "style",
      "font-size: 23px; color: #333;font-family:Arial, Helvetica, sans-serif; line-height:24px; padding: 18px 10px 8px; text-align: center; box-sizing: border-box;"
    );
    title.textContent = "Legacy Spa";

    const widget = document.createElement("div");
    widget.className = "vagaro";
    widget.setAttribute(
      "style",
      "width:250px; padding:0; border:0; margin:0 auto; text-align:center;"
    );
    widget.innerHTML = POWERED_BY_HTML;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = VAGARO_SCRIPT_SRC;
    script.async = true;
    widget.appendChild(script);

    container.appendChild(title);
    container.appendChild(widget);

    // Detect the injected iframe to clear the loading state.
    const observer = new MutationObserver(() => {
      if (container.querySelector("iframe")) {
        setLoading(false);
        observer.disconnect();
      }
    });
    observer.observe(container, { childList: true, subtree: true });
    const timeout = setTimeout(() => {
      setLoading(false);
      if (!container.querySelector("iframe")) setFailed(true);
    }, 6000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
      startedRef.current = false;
      if (container) container.innerHTML = "";
    };
  }, []);

  return (
    <div className="w-full">
      <div className="relative mx-auto w-full max-w-3xl overflow-x-auto rounded-3xl border border-white/10 bg-white p-4 md:p-6">
        {loading && (
          <div
            role="status"
            aria-live="polite"
            className="flex flex-col items-center justify-center gap-3 py-16 text-legacy-charcoal/70"
            data-testid="vagaro-loading"
          >
            <Loader2 className="h-7 w-7 animate-spin text-legacy-mint" aria-hidden="true" />
            <p className="text-sm">Loading the booking calendar…</p>
          </div>
        )}
        {failed && (
          <div
            role="alert"
            className="flex flex-col items-center justify-center gap-2 py-16 text-center text-legacy-charcoal/70"
            data-testid="vagaro-error"
          >
            <p className="font-medium text-legacy-charcoal">
              The booking calendar didn't load.
            </p>
            <p className="text-sm">
              Please use the "Open Booking in Vagaro" button below to book your
              appointment.
            </p>
          </div>
        )}
        <div
          ref={containerRef}
          className="vagaro-widget-container mx-auto w-full max-w-full"
          data-testid="vagaro-widget-container"
        />
      </div>

      {/* External fallback — always available. */}
      <div className="mx-auto mt-8 max-w-3xl text-center" data-testid="vagaro-fallback">
        <p className="text-white/60">Having trouble loading the booking calendar?</p>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-legacy-mint px-7 py-3.5 text-sm font-semibold text-legacy-charcoal transition-all hover:bg-legacy-mint/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-legacy-mint focus-visible:ring-offset-2 focus-visible:ring-offset-legacy-charcoal"
          data-testid="vagaro-fallback-btn"
          aria-label="Open booking in Vagaro (opens in a new tab)"
        >
          Open Booking in Vagaro
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
};
